// src/components/CardV2/media/FullBleedCard.tsx
/**
 * FullBleedCard — edge-to-edge gradient card for non-canonical content.
 * Header and footer render inside the gradient bg.
 * Receives renderHeader/renderFooter from CardV2 dispatcher.
 */

import React, { FC, useState } from 'react';
import { styled } from 'linaria/react';

import { FONTS } from '../../../styles/constants';
import { LayoutCardProps } from '../types';
import { deriveGradient } from '../primitives/colorUtils';
import { LAYOUT_CARD } from '../styled/tokens';

const StyledContainer = styled.div<{ gradientFrom: string; gradientTo: string }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  position: relative;
  overflow: hidden;
  padding: ${LAYOUT_CARD.FULL_BLEED_PADDING.top}px ${LAYOUT_CARD.FULL_BLEED_PADDING.sides}px
    ${LAYOUT_CARD.FULL_BLEED_PADDING.bottom}px;
  background: linear-gradient(180deg, ${({ gradientFrom }) => gradientFrom} 0%, ${({ gradientTo }) => gradientTo} 100%);
`;

const NOISE_DATA_URI = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23n)' opacity='0.15'/%3E%3C/svg%3E")`;

const StyledNoiseOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-image: ${NOISE_DATA_URI};
  background-repeat: repeat;
  opacity: ${LAYOUT_CARD.NOISE_OPACITY};
  pointer-events: none;
`;

const StyledDivider = styled.div`
  width: 100%;
  height: 1px;
  background: ${LAYOUT_CARD.DIVIDER_COLOR};
  position: relative;
  z-index: 1;
`;

const StyledContentArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  position: relative;
  z-index: 1;
  flex: 1;
`;

const StyledEyebrow = styled.div`
  font-family: ${FONTS.secondary};
  font-size: ${LAYOUT_CARD.EYEBROW_FONT_SIZE}px;
  font-weight: 400;
  line-height: 1.4;
  color: white;
`;

const StyledTitle = styled.div`
  font-family: ${FONTS.primary};
  font-size: ${LAYOUT_CARD.TITLE_FONT_SIZE.fullBleed}px;
  font-weight: 400;
  line-height: 1.3;
  color: white;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const StyledTitleExpanded = styled(StyledTitle)`
  flex: 1;
`;

const StyledLink = styled.a`
  display: block;
  text-decoration: none;
  cursor: pointer;
  color: inherit;
`;

const StyledImageFrame = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: ${LAYOUT_CARD.IMAGE_BORDER_RADIUS}px;
  overflow: hidden;
  position: relative;
`;

const StyledImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StyledHeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const StyledHeaderWrapper = styled.div`
  position: relative;
  z-index: 1;
`;

const StyledSourceDomain = styled.div`
  font-family: ${FONTS.secondary};
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4;
  color: rgba(255, 255, 255, ${LAYOUT_CARD.SOURCE_TEXT_OPACITY});
  flex: 1;
`;

const StyledFooterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 20px;
  width: 100%;
  position: relative;
  z-index: 1;
`;

const FullBleedCard: FC<LayoutCardProps> = ({
  title,
  imageUrl,
  backgroundColor,
  externalLink,
  eyebrow,
  sourceDomain,
  renderHeader,
  renderFooter,
  className,
}) => {
  const gradient = deriveGradient(backgroundColor);
  const [imgError, setImgError] = useState(false);
  const showImage = !!imageUrl && !imgError;

  const cardBody = (
    <StyledContainer className={className} gradientFrom={gradient.from} gradientTo={gradient.to}>
      <StyledNoiseOverlay />
      {renderHeader && <StyledHeaderWrapper>{renderHeader(backgroundColor)}</StyledHeaderWrapper>}
      <StyledDivider />
      <StyledContentArea>
        <StyledHeaderSection>
          {eyebrow && <StyledEyebrow>{eyebrow}</StyledEyebrow>}
          {showImage ? <StyledTitle>{title}</StyledTitle> : <StyledTitleExpanded>{title}</StyledTitleExpanded>}
        </StyledHeaderSection>
        {showImage && (
          <StyledImageFrame>
            <StyledImage src={imageUrl as string} alt={title} loading="lazy" onError={() => setImgError(true)} />
          </StyledImageFrame>
        )}
      </StyledContentArea>
      {renderFooter && (
        <StyledFooterRow>
          {sourceDomain && <StyledSourceDomain>{sourceDomain}</StyledSourceDomain>}
          {renderFooter('#c8c0ac')}
        </StyledFooterRow>
      )}
    </StyledContainer>
  );

  return externalLink ? (
    <StyledLink href={externalLink} target="_blank" rel="noopener noreferrer">
      {cardBody}
    </StyledLink>
  ) : (
    cardBody
  );
};

export default FullBleedCard;
